import { getCountry, isGcc, isPakistan } from '@/data/locales';
import {
  normalizeSupplierMobile,
  SUPPLIER_EMAIL_REGEX,
} from '@/utils/supplierValidation';
import { validatePhone } from '@/utils/mobileValidation';
import type { EntityImportConfig, EntityImportField } from '../types';

function regionLabel(country: string): string {
  if (country === 'US') return 'State';
  if (country === 'GB') return 'Region';
  if (country === 'AE') return 'Emirate';
  if (country === 'PK') return 'Province';
  return 'Province / State / Region';
}

function postalLabel(country: string): string {
  if (country === 'US') return 'ZIP Code';
  if (country === 'GB') return 'Postcode';
  return 'Postal Code';
}

function taxIdLabel(country: string): string {
  return getCountry(country).taxIdLabel || 'Tax ID';
}

function mobileHint(country: string): string {
  if (country === 'PK') {
    return 'Optional; Pakistani mobile e.g. 03001234567 — unique per company';
  }
  if (country === 'GB') {
    return 'Optional; UK phone e.g. 07123456789 — unique per company';
  }
  if (country === 'US') {
    return 'Optional; 10-digit US phone — unique per company';
  }
  return 'Optional; must be unique per company when provided';
}

/** Build import fields matching the store country (same set as backend template). */
export function buildCustomerImportFields(countryCode?: string | null): EntityImportField[] {
  const country = (countryCode || 'GB').toUpperCase();
  const fields: EntityImportField[] = [
    { fieldname: 'customer_name', label: 'Customer Name', required: true, fieldtype: 'Data' },
    {
      fieldname: 'customer_type',
      label: 'Customer Type',
      required: false,
      fieldtype: 'Data',
      hint: 'Retailer, Wholesale, or Distributor (default Retailer)',
    },
    {
      fieldname: 'mobile',
      label: 'Mobile',
      required: false,
      fieldtype: 'Data',
      hint: mobileHint(country),
    },
    { fieldname: 'email', label: 'Email', required: false, fieldtype: 'Data' },
    { fieldname: 'address', label: 'Address', required: false, fieldtype: 'Text' },
    { fieldname: 'city', label: 'City', required: false, fieldtype: 'Data' },
    {
      fieldname: 'province',
      label: regionLabel(country),
      required: false,
      fieldtype: 'Data',
    },
    {
      fieldname: 'postal_code',
      label: postalLabel(country),
      required: false,
      fieldtype: 'Data',
    },
  ];

  if (isPakistan(country)) {
    fields.push(
      {
        fieldname: 'cnic',
        label: 'CNIC',
        required: false,
        fieldtype: 'Data',
        hint: 'Optional; 13 digits without dashes',
      },
      {
        fieldname: 'ntn',
        label: 'NTN',
        required: false,
        fieldtype: 'Data',
        hint: 'Optional; 8 digits without dashes',
      },
    );
  } else if (isGcc(country)) {
    fields.push({
      fieldname: 'cnic',
      label: taxIdLabel(country),
      required: false,
      fieldtype: 'Data',
      hint: `Optional; ${taxIdLabel(country)}`,
    });
  }

  fields.push(
    {
      fieldname: 'credit_limit',
      label: 'Credit Limit',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional; max credit allowed (0 = none)',
    },
    {
      fieldname: 'opening_balance',
      label: 'Opening Balance',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional. + = customer owes you (outstanding). − = customer advance',
    },
  );

  return fields;
}

const HEADER_ALIASES: Record<string, string[]> = {
  customer_name: ['customer name', 'name', 'client', 'client name', 'customer'],
  customer_type: ['customer type', 'type', 'client type'],
  mobile: ['mobile', 'phone', 'phone number', 'contact', 'contact number', 'cellphone', 'cell'],
  email: ['email', 'email address', 'e-mail'],
  address: ['address', 'addr', 'location', 'street', 'street address'],
  city: ['city', 'city name', 'town'],
  province: [
    'province',
    'state',
    'region',
    'county',
    'emirate',
    'province / state / region',
    'state / province',
  ],
  postal_code: [
    'postal code',
    'postcode',
    'zip',
    'zip code',
    'zipcode',
    'postal / zip code',
  ],
  cnic: [
    'cnic',
    'cnic number',
    'national id',
    'cnic (fbr)',
    'tax id',
    'trn',
    'vat / trn',
    'vat number',
    'tin',
    'ein',
  ],
  ntn: ['ntn', 'ntn number', 'tax registration', 'ntn (fbr)', 'gst', 'strn'],
  credit_limit: ['credit limit', 'credit', 'max credit'],
  opening_balance: ['opening balance', 'opening amount', 'receivable', 'outstanding'],
};

export function buildCustomerImportConfig(countryCode?: string | null): EntityImportConfig {
  const country = (countryCode || 'GB').toUpperCase();

  return {
    entityId: 'customer',
    title: 'Import Customers',
    description: 'Download a template, map columns, then validate and import customers.',
    doctypeLabel: 'POS Customer',
    fields: buildCustomerImportFields(country),
    headerAliases: HEADER_ALIASES,
    localStorageKey: 'data-import:customer',
    onSuccessInvalidateKeys: [['customers']],
    skipReasonLabels: {
      invalid_mobile: 'Invalid mobile number',
      duplicate_mobile: 'Duplicate mobile in file',
      invalid_email: 'Invalid email',
      invalid_cnic: 'Invalid CNIC / tax ID',
      invalid_ntn: 'Invalid NTN',
      already_exists: 'Customer already exists for this company',
      manual_skip: 'Manually skipped',
      invalid_required: 'Required field missing',
    },
    normalizeRow: (raw) => {
      const mobileRaw = (raw.mobile || '').trim();
      const mobile = mobileRaw ? normalizeSupplierMobile(mobileRaw, country) : '';
      const cnicDigits = (raw.cnic || '').replace(/\D/g, '');
      const ntnDigits = (raw.ntn || '').replace(/\D/g, '');
      return {
        ...raw,
        customer_name: (raw.customer_name || '').trim(),
        customer_type: (raw.customer_type || 'Retailer').trim(),
        mobile,
        email: (raw.email || '').trim(),
        address: (raw.address || '').trim(),
        city: (raw.city || '').trim(),
        province: (raw.province || '').trim(),
        postal_code: (raw.postal_code || '').trim(),
        cnic: cnicDigits,
        ntn: ntnDigits,
        credit_limit: (raw.credit_limit || '').trim(),
        opening_balance: (raw.opening_balance || '').trim(),
      };
    },
    clientValidateRow: (row, ctx) => {
      const name = row.customer_name?.trim() || '';
      if (!name) {
        return { reason: 'invalid_required', label: 'Customer name is required' };
      }

      const mobile = row.mobile || '';
      if (mobile) {
        const phoneErr = validatePhone(mobile, country, { required: false });
        if (phoneErr) {
          return { reason: 'invalid_mobile', label: phoneErr };
        }
        const prev = ctx.seenMobiles.get(mobile);
        if (prev !== undefined) {
          return {
            reason: 'duplicate_mobile',
            label: `Duplicate mobile (also on row ${prev})`,
          };
        }
        ctx.seenMobiles.set(mobile, ctx.rowNumber);
      }

      if (row.email && !SUPPLIER_EMAIL_REGEX.test(row.email)) {
        return { reason: 'invalid_email', label: 'Enter a valid email address' };
      }

      if (isPakistan(country)) {
        const cnic = row.cnic || '';
        const ntn = row.ntn || '';
        if (cnic && cnic.length !== 13 && !(cnic.length === 8 && !ntn)) {
          return { reason: 'invalid_cnic', label: 'CNIC must be 13 digits' };
        }
        if (ntn && ntn.length !== 8) {
          return { reason: 'invalid_ntn', label: 'NTN must be 8 digits' };
        }
      }

      return null;
    },
    api: {
      downloadTemplate: '/method/saas_pos.api.data_import.api.download_template',
      importRows: '/method/saas_pos.api.data_import.api.import_rows',
    },
  };
}

/** Default GB config for modules that don't pass country yet. */
export const customerImportConfig = buildCustomerImportConfig('GB');
